import React from 'react';
import { ArrowRight, Check, X } from 'lucide-react';
import useInView from './useInView';

/**
 * Why nobody can prove who delivered the care — seed deck slide 24, drawn.
 *
 * Two lanes for the same encounter. Through an intermediary, the delivering
 * organization falls off at the hand-off: the plan sees the billing entity.
 * Through the ledger, the delivering organization rides along to the plan and
 * the remittance. The figures are the deck's; the organizations are generic.
 */

const STEPS_INTERMEDIARY = [
  { label: 'Community org delivers the service', keep: true },
  { label: 'Hub or intermediary files the claim', keep: true },
  { label: 'Plan records the billing entity', keep: false },
];

const STEPS_LEDGER = [
  { label: 'Community org records the encounter, with consent', keep: true },
  { label: 'WeaveIn submits it the plan’s own way', keep: true },
  { label: 'Plan and remittance carry the delivering org', keep: true },
];

const Lane: React.FC<{ title: string; steps: typeof STEPS_LEDGER; inView: boolean; tone: 'bad' | 'good'; delay: number }> = ({
  title,
  steps,
  inView,
  tone,
  delay,
}) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
    <p className={`text-sm font-semibold uppercase tracking-wider mb-4 ${tone === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-v2accent'}`}>
      {title}
    </p>
    <ol className="flex flex-col md:flex-row md:items-stretch gap-3">
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <li
            className={`flex-1 rounded-xl border p-4 text-sm transition-all duration-500 ${
 inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
 } ${s.keep ? 'border-gray-200 dark:border-gray-700' : 'border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30'}`}
            style={{ transitionDelay: `${delay + i * 350}ms` }}
          >
            <p className="text-gray-800 dark:text-gray-100 mb-2">{s.label}</p>
            <p className={`flex items-center gap-1 text-xs font-semibold ${s.keep ? 'text-v2accent' : 'text-red-600 dark:text-red-400'}`}>
              {s.keep ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : <X className="w-3.5 h-3.5" aria-hidden="true" />}
              {s.keep ? 'Delivering org on the record' : 'Delivering org lost'}
            </p>
          </li>
          {i < steps.length - 1 && <ArrowRight className="hidden md:block self-center w-5 h-5 text-gray-300 shrink-0" aria-hidden="true" />}
        </React.Fragment>
      ))}
    </ol>
  </div>
);

const AttributionGap: React.FC = () => {
  const { ref, inView } = useInView<HTMLDivElement>();
  return (
    <section className="py-16 bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">
          Today, nobody can prove who delivered the care.
        </h2>
        <p className="text-gray-600 dark:text-gray-300 max-w-3xl mb-8">
          46% of providers submit through a hub or a plan, and about one in two Community Supports services loses provider-level
          attribution because the billing entity is not the delivering one. When the state becomes the payer, that stops being an
          analytics problem: you cannot pay a provider you cannot identify.
        </p>
        <div ref={ref} className="space-y-5">
          <Lane title="Through an intermediary" steps={STEPS_INTERMEDIARY} inView={inView} tone="bad" delay={0} />
          <Lane title="Through the ledger" steps={STEPS_LEDGER} inView={inView} tone="good" delay={900} />
        </div>
      </div>
    </section>
  );
};

export default AttributionGap;
